import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback
} from 'react';
import classes from './CallPractice.module.scss';
import UserContext from '../../contexts/UserContext';
import socketHandler from '../../services/Socket';

import {
  createMicController,
  checkMicPermission,
  stopMicStream
} from './voiceDetection';

import { MicVAD } from '@ricky0123/vad-web';
import { Prompt } from './AICallingInterface';

interface CallPracticeProps {
  prompt?: Prompt;
}

const CallPractice = ({ prompt }: CallPracticeProps) => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [currentSpeaker, setCurrentSpeaker] = useState<'user' | 'ai' | null>(
    null
  );
  const [aiAudioLevel, setAiAudioLevel] = useState<number>(0);
  const [userAudioLevel, setUserAudioLevel] = useState<number>(0);

  const socket = socketHandler.getSocket();

  // const socketRef = useRef<Socket | null>(null);
  const vadRef = useRef<MicVAD | null>(null);

  //This use for tracking ai audio playback and volume, stop ai audio when call ends
  const aiAudioContextRef = useRef<AudioContext | null>(null);
  const aiAudioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const aiVolumeRAFRef = useRef<number | null>(null);
  const volumeIntervalRef = useRef<number | null>(null);

  const { user_bundle } = useContext(UserContext);
  const userName = user_bundle.user.first_name;
  const userAvatarUrl = user_bundle.user.profile_pic_url;

  const handleCallStart = async () => {
    const permission = await checkMicPermission();
    if (permission) {
      vadRef.current = await createMicController(
        handleSpeechStart,
        handleSpeechEnd,
        setUserAudioLevel,
        volumeIntervalRef
      );
      setIsCallActive(!isCallActive);
      if (!isCallActive) {
        setCurrentSpeaker(null);
      }

      socket.emit('start_call', prompt);
    } else {
      //do something if permission is not granted
      console.error('Microphone permission not granted');
    }
  };

  const handleCallEnd = useCallback(() => {
    //stop volume tracking interval
    volumeIntervalRef.current && clearInterval(volumeIntervalRef.current);

    //destroy user mic vad
    vadRef.current?.pause();
    vadRef.current?.destroy();
    stopMicStream();
    //stop ai voice
    if (aiAudioSourceRef.current) {
      aiAudioSourceRef.current.stop();
      aiAudioSourceRef.current.disconnect();
      aiAudioSourceRef.current = null;
    }
    if (aiAudioContextRef.current) {
      aiAudioContextRef.current.close();
      aiAudioContextRef.current = null;
    }
    if (aiVolumeRAFRef.current) {
      cancelAnimationFrame(aiVolumeRAFRef.current);
      aiVolumeRAFRef.current = null;
    }
    setIsCallActive(false);
    setCurrentSpeaker(null);

    // Emit end call event to server
    socket.emit('end_call');
  }, [socket]);

  useEffect(() => {
    //setup socket
    console.log('Setting up socket listeners');
    socket.on('ai_response', async (audioBuffer: ArrayBuffer) => {
      // Clean up any previous audio
      if (aiAudioSourceRef.current) {
        aiAudioSourceRef.current.stop();
        aiAudioSourceRef.current.disconnect();
        aiAudioSourceRef.current = null;
      }
      if (aiAudioContextRef.current) {
        aiAudioContextRef.current.close();
        aiAudioContextRef.current = null;
      }
      if (aiVolumeRAFRef.current) {
        cancelAnimationFrame(aiVolumeRAFRef.current);
        aiVolumeRAFRef.current = null;
      }

      const audioCtx = new AudioContext();
      aiAudioContextRef.current = audioCtx;

      const decodedAudio = await audioCtx.decodeAudioData(audioBuffer.slice(0));
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      const data = new Uint8Array(analyser.fftSize);
      let sourceEnded = false;

      const source = audioCtx.createBufferSource();
      source.buffer = decodedAudio;
      source.connect(analyser);
      analyser.connect(audioCtx.destination);
      aiAudioSourceRef.current = source;

      source.start();
      setCurrentSpeaker('ai');
      trackVolume();

      function trackVolume() {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        const volume = rms * 100 * 10;
        setAiAudioLevel(volume > 100 ? 100 : volume);

        if (!sourceEnded) {
          aiVolumeRAFRef.current = requestAnimationFrame(trackVolume);
        }
      }

      source.onended = () => {
        sourceEnded = true;
        aiAudioSourceRef.current = null;
        if (aiAudioContextRef.current) {
          aiAudioContextRef.current.close();
          aiAudioContextRef.current = null;
        }
        if (aiVolumeRAFRef.current) {
          cancelAnimationFrame(aiVolumeRAFRef.current);
          aiVolumeRAFRef.current = null;
        }
      };
    });

    socket.on('connect_error', (err: unknown) => {
      console.error('Socket connection error:', err);
    });
    socket.on('disconnect', () => {
      console.log('Socket disconnected');
    });
    socket.on('error', (error: any) => {
      console.error('socket error :', error);
    });

    //cleanup on unmount component
    return () => {
      vadRef.current?.pause();
      vadRef.current?.destroy();
      handleCallEnd();
    };
  }, [handleCallEnd, socket]);

  function handleSpeechStart() {
    // setIsSpeaking(true);
    setCurrentSpeaker('user');
  }

  function handleSpeechEnd(audio: Int16Array) {
    setUserAudioLevel(0);
    socket.emit('user_speech', audio);
  }

  return (
    <>
      {/* Main interface */}
      <div className={classes['interfaceContainer']}>
        {/* User Avatar */}
        <div className={classes['avatarContainer']}>
          <div
            className={[
              classes.avatar,
              classes.userAvatar,
              currentSpeaker === 'user' ? classes.speakingAvatar : ''
            ].join(' ')}
          >
            <div className={classes.avatarInner}>
              {userAvatarUrl ? (
                <img
                  src={userAvatarUrl}
                  alt={userName}
                  className={classes.avatarImg}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display =
                      'none';
                  }}
                />
              ) : (
                <span className={classes.avatarText}>{userName}</span>
              )}
            </div>

            {currentSpeaker === 'user' && (
              <div
                className={classes.voiceRing}
                style={{
                  opacity: userAudioLevel > 0 ? 1 : 0.3,
                  boxShadow:
                    userAudioLevel > 1
                      ? '0 0 50px rgba(79, 70, 229, 0.5)'
                      : 'none'
                }}
              />
            )}
          </div>
        </div>

        {/* Center Control */}
        <div className={classes.centerContainer}>
          <div
            className={classes.outerBorder}
            style={
              currentSpeaker === 'user'
                ? {
                    border: '2px solid rgba(79, 70, 229, 0.5)',
                    boxShadow:
                      userAudioLevel > 2
                        ? `0 0 ${userAudioLevel * 5}px rgba(79, 70, 229, 0.8)`
                        : 'none'
                  }
                : currentSpeaker === 'ai'
                ? {
                    border: 'rgba(6, 214, 160, 0.5)',
                    boxShadow:
                      aiAudioLevel > 3
                        ? `0 0 ${aiAudioLevel * 5}px rgba(6, 214, 160, 0.5)`
                        : 'none'
                  }
                : {}
            }
          >
            <div
              className={[
                classes.centerCircle,
                currentSpeaker ? classes.activeCenter : '',
                currentSpeaker === 'user'
                  ? classes.userBackground
                  : currentSpeaker === 'ai'
                  ? classes.aiBackground
                  : classes.defaultBackground
              ].join(' ')}
            >
              {/* <div
                  className={classes.buttonRing}
                  style={
                    currentSpeaker
                      ? {
                          opacity: 1,
                          transform: `scale(${1.2 + audioLevel / 300})`
                        }
                      : {}
                  }
                /> */}
              <button
                onClick={isCallActive ? handleCallEnd : handleCallStart}
                className={[
                  classes.startButton,
                  isCallActive ? classes.activeButton : ''
                ].join(' ')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <span className={classes.buttonText}>
                  {isCallActive ? 'END' : 'START'}
                </span>
              </button>

              {/* Voice visualization */}
              {/* {isCallActive && currentSpeaker && (
                  <div className={classes.voiceVisualization}>
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={i}
                        className={classes.voiceBar}
                        style={{
                          height: `${
                            10 +
                            (userAudioLevel || aiAudioLevel * Math.random()) / 3
                          }px`,
                          animationDelay: `${i * 0.1}s`
                        }}
                      />
                    ))}
                  </div>
                )} */}
            </div>
          </div>
        </div>

        {/* AI Avatar */}
        <div className={classes.avatarContainer}>
          <div
            className={[
              classes.avatar,
              classes.aiAvatar,
              currentSpeaker === 'ai' ? classes.speakingAvatar : ''
            ].join(' ')}
          >
            <div className={classes.avatarInner}>
              <img
                src='https://img.freepik.com/premium-photo/ai-round-avatar-created-with-generative-ai_916303-436.jpg'
                alt='AI Avatar'
                className={classes.avatarImg}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
            {currentSpeaker === 'ai' && (
              <div
                className={classes.voiceRing}
                style={{
                  opacity: aiAudioLevel > 2 ? 1 : 0.3,
                  boxShadow:
                    aiAudioLevel > 3
                      ? '0 0 50px rgba(6, 214, 160, 0.5)'
                      : 'none'
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Status indicator */}
      <div className={classes.statusContainer}>
        <div
          className={classes.statusDot}
          style={{
            backgroundColor: isCallActive ? '#06d6a0' : '#6b7280'
          }}
        />
        <span className={classes.statusText}>
          {isCallActive ? 'Call Active' : 'Ready to Connect'}
        </span>
      </div>
    </>
  );
};

export default CallPractice;
